import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  getHistory,
  updateHistoryItem,
  deleteHistoryItem,
  getContentTypes,
  ContentHistoryItem,
  ContentTypeCount,
} from '../services/backendApi';

interface HistoryPanelProps {
  onSelectItem?: (item: ContentHistoryItem) => void;
}

// Content type labels and icons
const contentTypeLabels: Record<string, { label: string; icon: string }> = {
  simulation: { label: 'Simulation', icon: '📊' },
  training: { label: 'Schulung', icon: '📚' },
  documentation: { label: 'Dokumentation', icon: '📄' },
  quiz: { label: 'Quiz', icon: '❓' },
  video_script: { label: 'Video-Skript', icon: '🎬' },
  slides: { label: 'Präsentation', icon: '🖼️' },
  templates: { label: 'Templates', icon: '📝' },
};

export const HistoryPanel: React.FC<HistoryPanelProps> = ({ onSelectItem }) => {
  const [items, setItems] = useState<ContentHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentTypes, setContentTypes] = useState<ContentTypeCount[]>([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [showFavorites, setShowFavorites] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ContentHistoryItem | null>(null);
  const [previewItem, setPreviewItem] = useState<ContentHistoryItem | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getHistory({
        contentType: selectedType || undefined,
        isFavorite: showFavorites ? true : undefined,
        search: searchQuery || undefined,
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
      });
      setItems(response.items);
      setTotal(response.total);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('Historie konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [selectedType, showFavorites, searchQuery, page]);

  const loadContentTypes = useCallback(async () => {
    try {
      const types = await getContentTypes();
      setContentTypes(types);
    } catch (err) {
      console.error('Failed to load content types:', err);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    loadContentTypes();
  }, [loadContentTypes]);

  const handleToggleFavorite = async (item: ContentHistoryItem) => {
    try {
      const updated = await updateHistoryItem(item.id, { isFavorite: !item.is_favorite });
      setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    } catch (err) {
      console.error('Failed to update favorite:', err);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await deleteHistoryItem(itemToDelete.id);
      setItems(prev => prev.filter(i => i.id !== itemToDelete.id));
      setTotal(prev => prev - 1);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  const handleCopyContent = (item: ContentHistoryItem) => {
    const content = typeof item.output_data.content === 'string' 
      ? item.output_data.content 
      : JSON.stringify(item.output_data, null, 2);
    navigator.clipboard.writeText(content);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffHours < 1) {
      const minutes = Math.floor(diffMs / (1000 * 60));
      return `vor ${minutes} Min.`;
    } else if (diffHours < 24) {
      return `vor ${Math.floor(diffHours)} Std.`;
    } else if (diffDays < 7) {
      return `vor ${Math.floor(diffDays)} Tagen`;
    } else {
      return date.toLocaleDateString('de-DE', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    }
  };

  const getTypeInfo = (type: string) => {
    return contentTypeLabels[type] || { label: type, icon: '📁' };
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          📜 Content-Historie
        </Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={loadHistory} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Suchen..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
          }}
          sx={{ flexGrow: 1, minWidth: 150 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Typ</InputLabel>
          <Select
            value={selectedType}
            label="Typ"
            onChange={(e) => {
              setSelectedType(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Alle</MenuItem>
            {contentTypes.map(ct => (
              <MenuItem key={ct.content_type} value={ct.content_type}>
                {getTypeInfo(ct.content_type).icon} {getTypeInfo(ct.content_type).label} ({ct.count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          icon={showFavorites ? <StarIcon /> : <StarBorderIcon />}
          label="Favoriten"
          onClick={() => {
            setShowFavorites(!showFavorites);
            setPage(1);
          }}
          color={showFavorites ? 'primary' : 'default'}
          variant={showFavorites ? 'filled' : 'outlined'}
          size="small"
        />
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Typography>Keine Einträge gefunden</Typography>
          <Typography variant="caption">
            {searchQuery || selectedType || showFavorites 
              ? 'Versuche andere Filter' 
              : 'Generierte Inhalte erscheinen hier'}
          </Typography>
        </Box>
      ) : (
        <>
          <List sx={{ flexGrow: 1, overflow: 'auto' }}>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    py: 1.5,
                  }}
                  onClick={() => {
                    if (onSelectItem) {
                      onSelectItem(item);
                    } else {
                      setPreviewItem(item);
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getTypeInfo(item.content_type).icon} {item.title || 'Ohne Titel'}
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip 
                            label={getTypeInfo(item.content_type).label} 
                            size="small" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography component="span" variant="caption" color="text.secondary">
                            {formatDate(item.created_at)}
                          </Typography>
                          {item.format && (
                            <Chip 
                              label={item.format} 
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      </React.Fragment>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Kopieren">
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyContent(item);
                        }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={item.is_favorite ? 'Favorit entfernen' : 'Als Favorit markieren'}>
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(item);
                        }}
                      >
                        {item.is_favorite ? <StarIcon color="warning" fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemToDelete(item);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(_, newPage) => setPage(newPage)}
                size="small"
              />
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eintrag löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du "{itemToDelete?.title || 'diesen Eintrag'}" wirklich löschen?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={!!previewItem} 
        onClose={() => setPreviewItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {previewItem && getTypeInfo(previewItem.content_type).icon}
            {previewItem?.title || 'Ohne Titel'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewItem && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={getTypeInfo(previewItem.content_type).label} size="small" />
                <Chip label={formatDate(previewItem.created_at)} size="small" variant="outlined" />
                {previewItem.format && <Chip label={previewItem.format} size="small" />}
                {previewItem.tags?.map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Eingabe-Parameter:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, mb: 2, bgcolor: 'action.hover' }}>
                <pre style={{ margin: 0, fontSize: '0.75rem', overflow: 'auto' }}>
                  {JSON.stringify(previewItem.input_data, null, 2)}
                </pre>
              </Paper>
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Generierter Inhalt:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                {typeof previewItem.output_data.content === 'string' ? (
                  <Typography 
                    variant="body2" 
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                  >
                    {previewItem.output_data.content}
                  </Typography>
                ) : (
                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                    {JSON.stringify(previewItem.output_data, null, 2)}
                  </pre>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<CopyIcon />}
            onClick={() => previewItem && handleCopyContent(previewItem)}
          >
            Kopieren
          </Button>
          <Button onClick={() => setPreviewItem(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HistoryPanel;
